// client/src/OfflineInteractiveMap.js
import React, { useRef, useEffect, useState } from 'react';
import { MapContainer, ImageOverlay } from 'react-leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import L from 'leaflet';
import POILayer from './Interactive_parts/OfflinePOILayer';
import { Eye } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import FitImageBounds from './Interactive_parts/FitImageBounds.js';
import MouseCoords from './Interactive_parts/MouseCoords.js';
import SetZoomControlPosition from './Interactive_parts/SetZoomControlPosition.js';
import ClickHandler from './Interactive_parts/ClickHandler.js';
import CoordPopup from './Interactive_parts/CoordPopup.js';
import isMobileScreen from './Interactive_parts/isMobileScreen.js';
import MapClickPopupCloser from './Interactive_parts/MapClickPopupCloser.js';
import { POISelectedIdContext } from './Interactive_parts/POISelectedIdContext.js';
import OfflineCreatePOI from './Interactive_parts/OfflineCreatePOI.js';
import GeomanShapesSync from './Interactive_parts/OfflineGeomanShapesSync.js';
import GeomanControl from './Interactive_parts/GeomanControl.js';
import ShapeEditToolbar from './Interactive_parts/OfflineShapeEditToolbar';
import OfflineBackup from '../components//uploadtools/OfflineBackup';
import OfflineUploadToServer from './Interactive_parts/OfflineUploadToServer';
import {
  getOfflineMap,
  updateOfflineMap,
  updateZone,
  deleteZone
} from '../utils/offlineDb';

function useResetMapOnChange(mapRef, imageSize, mapData) {
  const prevId = useRef();
  useEffect(() => {
    if (!mapRef.current || !imageSize || !mapData?.id) return;
    const map = mapRef.current;
    const bounds = [
      [0, 0],
      [imageSize[0], imageSize[1]]
    ];
    map.setMaxBounds(bounds);
    map.fitBounds(bounds, { animate: false, padding: [0, 0] });
    map.setZoom(0);
    map.panTo([(imageSize[0] / 2), (imageSize[1] / 2)], { animate: false });
    prevId.current = mapData.id;
  }, [mapData?.id, imageSize]);
}

function normalizeZoneKeys(zone) {
  let out = { ...zone };
  if (typeof out.strokeOpacity !== "undefined") {
    out.stroke_opacity = out.strokeOpacity;
    delete out.strokeOpacity;
  }
  if (typeof out.fillOpacity !== "undefined") {
    out.fill_opacity = out.fillOpacity;
    delete out.fillOpacity;
  }
  delete out.strokeOpacity;
  delete out.fillOpacity;
  return out;
}

function InteractiveMap(props) {
  const {
    mapData, imageSize, pois, visibleTypes, visibleCategories, hoveredType, hoveredPoiId, setHoveredPoiId, pendingPOI, setPendingPOI, isMapOwnerOrFounder, handleMapCoordClick, handleClick: userHandleClick, mouseCoord, handleMouseCoord, shareCoord, handleCopyCoord, copiedCoord, customCategories, typeColours, customTypes, selectedCategory, setSelectedCategory, selectedType, setSelectedType, typeColourInput, setTypeColourInput, shape, setShape, description, setDescription,
    showAllPoiLabels, setShowAllPoiLabels, onToggleFound, onCategoryUpdate, user, mapOwnerId, isEmbed, hideSidebarHover, setHideSidebarHover,
    commentsOpen,
    commentsWidth,
    poiName, setPoiName,
    visibleZoneIds,
    onZonesChanged,
    hoveredZoneId,
    imageUrl,
    offlineImageUrl,
    offlinePois,
    setOfflinePois,
    offlineShapes,
    onSaveCategory,
    onSaveType,
    onSavePOI,
    setCategories,
    setTypes
  } = props;
  const offlineMode = true;

  const [isGeomanActive, setIsGeomanActive] = useState(false);
  const [showFound, setShowFound] = useState(false);
  const [localLabelBorder, setLocalLabelBorder] = useState(1);
  const [localLabelBorderColour, setLocalLabelBorderColour] = useState("#000000");
  const [showCreatePOIModal, setShowCreatePOIModal] = useState(false);
  const [editPOIModalOpen, setEditPOIModalOpen] = useState(false);
  const [infoPOIModalOpen, setInfoPOIModalOpen] = useState(false);
  const [poiImageUrl, setPoiImageUrl] = useState("");
  const [localTypeColour, setLocalTypeColour] = useState(typeColourInput);
  const [colourLocked, setColourLocked] = useState(false);
  const [selectedPoiId, setSelectedPoiId] = useState(null);
  const [selectedShape, setSelectedShape] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [savingShape, setSavingShape] = useState(false);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [poiImageFile, setPoiImageFile] = useState(null);
  const [poiImagePreview, setPoiImagePreview] = useState(null);
  const fileInputRef = useRef();
  const poiImageInputRef = useRef();
  const shapeLayerMap = useRef({});
  const allLayersById = useRef({});
  const prevOriginalStyles = useRef({});
  const animationRef = useRef(null);
  const glowOnRef = useRef(true);

  const [isMobile, setIsMobile] = useState(isMobileScreen());
  const [toolboxBottom, setToolboxBottom] = useState(98);

  const [stylerOfflineInfoOpen, setStylerOfflineInfoOpen] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(isMobileScreen());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setToolboxBottom(98);
      return;
    }
    function updateToolboxBottom() {
      const bar = document.getElementById('mobile-tab-bar');
      if (bar) {
        const rect = bar.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const offset = Math.max(windowHeight - rect.top, 0);
        setToolboxBottom(offset + 5);
      } else {
        setToolboxBottom(25);
      }
    }
    updateToolboxBottom();
    window.addEventListener('resize', updateToolboxBottom);
    return () => window.removeEventListener('resize', updateToolboxBottom);
  }, [isMobile]);

  useEffect(() => {
    setShowCreatePOIModal(!!pendingPOI && !isGeomanActive);
  }, [pendingPOI, isGeomanActive]);

  useEffect(() => {
    if (!offlineMode || !mapData?.id) return;
    const handleOfflineMapChange = async () => {
      try {
        const offlineMap = await getOfflineMap(mapData.id);
        const poisArray = Array.isArray(offlineMap?.pois) ? offlineMap.pois : [];
        setOfflinePois(poisArray);
      } catch (err) { }
    };
    window.addEventListener('offlineMapDataChanged', handleOfflineMapChange);
    return () => window.removeEventListener('offlineMapDataChanged', handleOfflineMapChange);
  }, [offlineMode, mapData?.id, setOfflinePois]);

  const foundPOIsKey = mapData?.id ? `found-pois-${mapData.id}` : null;
  const [foundPOIsState, setFoundPOIsState] = useState(() => {
    if (!foundPOIsKey) return [];
    try {
      const stored = localStorage.getItem(foundPOIsKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (!foundPOIsKey) return;
    try {
      localStorage.setItem(foundPOIsKey, JSON.stringify(foundPOIsState));
    } catch { }
  }, [foundPOIsState, foundPOIsKey]);

  useEffect(() => {
    if (animationRef.current) {
      clearTimeout(animationRef.current);
      animationRef.current = null;
    }
    if (!hoveredZoneId || !shapeLayerMap.current[hoveredZoneId]) {
      Object.entries(prevOriginalStyles.current).forEach(([id, style]) => {
        const layer = shapeLayerMap.current[id];
        if (layer && layer.setStyle) layer.setStyle(style);
        if (layer && layer._path) layer._path.style.filter = '';
      });
      prevOriginalStyles.current = {};
      return;
    }
    const layer = shapeLayerMap.current[hoveredZoneId];
    if (!layer || !layer.setStyle) return;
    if (!prevOriginalStyles.current[hoveredZoneId]) {
      prevOriginalStyles.current[hoveredZoneId] = {
        color: layer.options.color,
        fillColor: layer.options.fillColor,
        opacity: layer.options.opacity,
        fillOpacity: layer.options.fillOpacity,
        weight: layer.options.weight,
        dashArray: layer.options.dashArray || '',
      };
    }
    const cycleGlow = () => {
      if (!layer || !layer.setStyle) return;
      if (glowOnRef.current) {
        layer.setStyle({
          color: '#fff',
          fillColor: '#fff',
          opacity: 0.95,
          fillOpacity: 0.27,
          weight: 5,
          dashArray: '4 4',
        });
        if (layer._path) {
          layer._path.style.filter = 'drop-shadow(0 0 6px #fff) drop-shadow(0 0 14px #fff)';
          layer._path.style.transition = 'filter 0.5s, stroke 0.5s, fill 0.5s';
        }
      } else {
        const style = prevOriginalStyles.current[hoveredZoneId];
        layer.setStyle(style);
        if (layer._path) layer._path.style.filter = '';
      }
      glowOnRef.current = !glowOnRef.current;
      animationRef.current = setTimeout(cycleGlow, 1000);
    };
    cycleGlow();
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
        animationRef.current = null;
      }
      if (layer && layer.setStyle && prevOriginalStyles.current[hoveredZoneId]) {
        layer.setStyle(prevOriginalStyles.current[hoveredZoneId]);
        if (layer._path) layer._path.style.filter = '';
        delete prevOriginalStyles.current[hoveredZoneId];
      }
    };
  }, [hoveredZoneId]);

  useEffect(() => {
    if (!visibleZoneIds || !allLayersById.current || !leafletMapRef.current) return;
    const map = leafletMapRef.current;
    Object.entries(allLayersById.current).forEach(([id, layer]) => {
      const isVisible = visibleZoneIds.has(Number(id));
      if (isVisible) {
        if (!map.hasLayer(layer)) map.addLayer(layer);
      } else {
        if (map.hasLayer(layer)) map.removeLayer(layer);
      }
    });
  }, [visibleZoneIds]);

  const mapDivRef = useRef();
  const leafletMapRef = useRef(null);

  useResetMapOnChange(leafletMapRef, imageSize, mapData);

  useEffect(() => {
    function handleMouseMove(e) {
      const t = e.target;
      if (
        !t.closest('.leaflet-marker-icon') &&
        !t.closest('.leaflet-popup')
      ) {
        if (hoveredPoiId !== null) setHoveredPoiId(null);
      }
    }
    const el = mapDivRef.current;
    if (!el) return;
    el.addEventListener('mousemove', handleMouseMove);
    return () => el.removeEventListener('mousemove', handleMouseMove);
  }, [hoveredPoiId, setHoveredPoiId]);

  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    const onDrawStart = () => setIsGeomanActive(true);
    const onDrawEnd = () => setIsGeomanActive(false);
    const onEditStart = () => setIsGeomanActive(true);
    const onEditEnd = () => setIsGeomanActive(false);
    const onCutStart = () => setIsGeomanActive(true);
    const onCutEnd = () => setIsGeomanActive(false);
    map.on('pm:drawstart', onDrawStart);
    map.on('pm:drawend', onDrawEnd);
    map.on('pm:editstart', onEditStart);
    map.on('pm:editend', onEditEnd);
    map.on('pm:cutstart', onCutStart);
    map.on('pm:cutend', onCutEnd);

    const onPmUpdate = e => {
      if (!selectedShape) return;
      const layer = e.layer;
      if (!layer) return;
      const geojson = layer.toGeoJSON();
      setSelectedShape(prev => ({
        ...prev,
        geometry: geojson.geometry,
        strokeColor: layer.options.color,
        fillColor: layer.options.fillColor,
        strokeOpacity: layer.options.opacity,
        fillOpacity: layer.options.fillOpacity,
        radius: layer.options.radius ?? prev.radius,
        pattern: prev.pattern,
        shapeType: prev.shapeType,
        label: prev.label,
        id: prev.id
      }));
      setSelectedLayer(layer);
    };

    map.on('pm:update', onPmUpdate);

    return () => {
      map.off('pm:drawstart', onDrawStart);
      map.off('pm:drawend', onDrawEnd);
      map.off('pm:editstart', onEditStart);
      map.off('pm:editend', onEditEnd);
      map.off('pm:cutstart', onCutStart);
      map.off('pm:cutend', onCutEnd);
      map.off('pm:update', onPmUpdate);
    };
  }, [leafletMapRef.current, selectedShape]);

  const isAnyModalOpen = () => (
    showCreatePOIModal === true ||
    editPOIModalOpen === true ||
    infoPOIModalOpen === true
  );

  function handleClick(coord, event) {
    if (event && event.originalEvent && event.originalEvent._shapeClick) {
      return;
    }
    if (typeof userHandleClick === "function") {
      userHandleClick(coord, event);
    }
  }

  function wrapIfModalOpen(fn) {
    return (...args) => {
      if (isAnyModalOpen()) return;
      return fn(...args);
    };
  }

  function getCategoryIdByName(name, categories) {
    if (!name || !categories) return null;
    let cat = categories.find(
      c => ((c && c.name ? c.name : c) || '').toLowerCase() === name.toLowerCase()
    );
    return cat ? (cat.id || cat) : null;
  }
  function getTypeIdByName(name, types, categoryId) {
    if (!name || !types) return null;
    let t = types.find(
      t =>
        ((t && t.name ? t.name : t) || '').toLowerCase() === name.toLowerCase() &&
        String(t.category_id) === String(categoryId)
    );
    return t ? (t.id || t) : null;
  }

  function isBlobLike(v) {
    return typeof Blob !== 'undefined' && v instanceof Blob;
  }
  function isDataUrl(v) {
    return typeof v === 'string' && /^data:image\/[a-zA-Z]+;base64,/.test(v);
  }
  function isHttpUrl(v) {
    return typeof v === 'string' && /^(https?:)?\/\//.test(v);
  }

  async function savePOI(name, imgUrl, iconFileArg) {
    if (!pendingPOI) {
      return;
    }
    let offlineMap = await getOfflineMap(mapData.id);
    let categories = Array.isArray(offlineMap?.poi_categories) ? offlineMap.poi_categories : [];
    let types = Array.isArray(offlineMap?.poi_types) ? offlineMap.poi_types : [];

    let finalCategory = getCategoryIdByName(selectedCategory, categories);
    if (!finalCategory && selectedCategory) {
      finalCategory = Date.now();
      const newCategory = { id: finalCategory, name: selectedCategory, map_id: mapData.id };
      if (typeof onSaveCategory === 'function') {
        await onSaveCategory(newCategory);
      }
      categories = [...categories, newCategory];
      await updateOfflineMap(mapData.id, om => ({ ...om, poi_categories: categories }));
      offlineMap = await getOfflineMap(mapData.id);
      categories = Array.isArray(offlineMap?.poi_categories) ? offlineMap.poi_categories : [];
      finalCategory = getCategoryIdByName(selectedCategory, categories);
      if (typeof setCategories === "function") setCategories(categories);
      if (!finalCategory) {
        throw new Error('Offline: Category could not be created');
      }
    }

    types = Array.isArray(offlineMap?.poi_types) ? offlineMap.poi_types : [];
    let typeList = types.filter(t => String(t.category_id) === String(finalCategory));
    let finalType = getTypeIdByName(selectedType, typeList, finalCategory);
    if (!finalType && selectedType) {
      finalType = Date.now() + 1;
      const newType = {
        id: finalType,
        name: selectedType,
        category_id: finalCategory,
        map_id: mapData.id,
        color: localTypeColour,
        shape,
        label_border: localLabelBorder,
        label_border_colour: localLabelBorderColour,
      };
      if (typeof onSaveType === 'function') {
        await onSaveType(newType);
      }
      types = [...types, newType];
      await updateOfflineMap(mapData.id, om => ({ ...om, poi_types: types }));
      offlineMap = await getOfflineMap(mapData.id);
      types = Array.isArray(offlineMap?.poi_types) ? offlineMap.poi_types : [];
      typeList = types.filter(t => String(t.category_id) === String(finalCategory));
      finalType = getTypeIdByName(selectedType, typeList, finalCategory);
      if (typeof setTypes === "function") setTypes(types);
      if (!finalType) {
        throw new Error('Offline: Type could not be created');
      }
    }

    if (types && types.length > 0 && finalType) {
      types = types.map(t =>
        String(t.id) === String(finalType)
          ? {
            ...t,
            color: localTypeColour,
            shape,
            label_border: localLabelBorder,
            label_border_colour: localLabelBorderColour,
          }
          : t
      );
      await updateOfflineMap(mapData.id, om => ({ ...om, poi_types: types }));
      if (typeof setTypes === "function") setTypes(types);
    }

    const basePOI = {
      id: Date.now() + Math.floor(Math.random() * 10000),
      poi_name: name || "",
      x: pendingPOI.x,
      y: pendingPOI.y,
      type_id: finalType,
      description,
      icon_file: "",
      map_id: mapData.id,
      image_url: "",
    };

    const finalizeSave = async (poiData) => {
      setPendingPOI(null);
      offlineMap = await getOfflineMap(mapData.id);
      let pois = Array.isArray(offlineMap?.pois) ? offlineMap.pois : [];
      pois = [...pois, poiData];
      await updateOfflineMap(mapData.id, om => ({ ...om, pois }));
      if (typeof setOfflinePois === "function") {
        setOfflinePois(pois);
      }
      setPoiName("");
      setDescription("");
      setSelectedCategory("");
      setSelectedType("");
      setIconFile(null);
      setIconPreview(null);
      setPoiImageFile(null);
      setPoiImagePreview(null);
      setLocalLabelBorder(1);
      setLocalLabelBorderColour("#000000");
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (poiImageInputRef.current) poiImageInputRef.current.value = '';
    };

    function finalizeWithImage(imageValue) {
      basePOI.image_url = imageValue || "";

      if (iconFileArg == null) {
        finalizeSave(basePOI);
        return;
      }

      if (isBlobLike(iconFileArg)) {
        const reader = new FileReader();
        reader.onload = async () => {
          basePOI.icon_file = reader.result || "";
          await finalizeSave(basePOI);
        };
        reader.readAsDataURL(iconFileArg);
        return;
      }

      if (isDataUrl(iconFileArg) || isHttpUrl(iconFileArg)) {
        basePOI.icon_file = iconFileArg;
        finalizeSave(basePOI);
        return;
      }

      finalizeSave(basePOI);
    }

    if (poiImageFile && isBlobLike(poiImageFile)) {
      const reader = new FileReader();
      reader.onload = () => {
        finalizeWithImage(reader.result);
      };
      reader.readAsDataURL(poiImageFile);
    } else if (isDataUrl(poiImageFile) || isHttpUrl(poiImageFile)) {
      finalizeWithImage(poiImageFile);
    } else if (isDataUrl(poiImagePreview) || isHttpUrl(poiImagePreview)) {
      finalizeWithImage(poiImagePreview);
    } else if (typeof imgUrl === 'string' && (isDataUrl(imgUrl) || isHttpUrl(imgUrl))) {
      finalizeWithImage(imgUrl);
    } else {
      finalizeWithImage("");
    }
  }

  async function handlePOIUpdate(updatedPOI, deletedPOIId = null) {
    if (!mapData?.id) {
      return;
    }
    let offlineMap = await getOfflineMap(mapData.id);
    let pois = Array.isArray(offlineMap?.pois) ? offlineMap.pois : [];
    if (deletedPOIId !== null) {
      pois = pois.filter(p => p.id !== deletedPOIId);
    } else if (updatedPOI) {
      const idx = pois.findIndex(p => p.id === updatedPOI.id);
      if (idx !== -1) {
        pois[idx] = updatedPOI;
      } else {
        pois.push(updatedPOI);
      }
    }
    await updateOfflineMap(mapData.id, om => ({ ...om, pois }));
    if (typeof setOfflinePois === "function") {
      setOfflinePois(pois);
    }
  }

  async function handleShapeToolbarSave(shape, layer) {
    setSavingShape(true);
    if (layer) {
      layer.setStyle({
        color: shape.strokeColor,
        fillColor: shape.fillColor,
        opacity: shape.strokeOpacity,
        fillOpacity: shape.fillOpacity,
      });
    }
    const cleanShape = normalizeZoneKeys({
      ...shape,
      strokeColor: shape.strokeColor,
      fillColor: shape.fillColor,
      strokeOpacity: shape.strokeOpacity,
      fillOpacity: shape.fillOpacity,
      label: shape.label,
      radius: shape.radius,
      pattern: shape.pattern,
      geometry: shape.geometry,
      shapeType: shape.shapeType,
      id: shape.id
    });
    await updateZone(mapData.id, cleanShape);
    setSavingShape(false);
    setSelectedShape(null);
    setSelectedLayer(null);
    setIsGeomanActive(false);
    setPendingPOI(null);
    if (typeof onZonesChanged === "function") onZonesChanged();
  }

  async function handleShapeToolbarDelete(shape, layer) {
    if (layer && leafletMapRef.current) {
      leafletMapRef.current.removeLayer(layer);
    }
    await deleteZone(mapData.id, shape.id);
    setSelectedShape(null);
    setSelectedLayer(null);
    setIsGeomanActive(false);
    setPendingPOI(null);
    if (typeof onZonesChanged === "function") onZonesChanged();
  }

  if (!imageSize || !mapData) {
    return (
      <div style={{
        color: "#fff", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", height: "100%"
      }}>
        Loading map...
      </div>
    );
  }

  const mapImageToShow = offlineMode && offlineImageUrl ? offlineImageUrl : (imageUrl || mapData.image_url);

  let poisToShow;
  if (offlineMode && offlinePois && Array.isArray(offlinePois)) {
    let offlineCats = [];
    let offlineTypes = [];
    if (customCategories && Array.isArray(customCategories)) {
      offlineCats = customCategories;
    }
    if (customTypes && Array.isArray(customTypes)) {
      offlineTypes = customTypes;
    }
    const catMap = {};
    for (const cat of offlineCats) catMap[cat.id] = cat;
    const typeMap = {};
    for (const t of offlineTypes) typeMap[t.id] = t;
    poisToShow = offlinePois.map(poi => {
      const type = typeMap[poi.type_id];
      return {
        ...poi,
        id: poi.id || `${poi.type_id}_${poi.x}_${poi.y}_${poi.poi_name || ''}`,
        name: poi.poi_name || poi.name || "Unnamed",
        type: poi.type_id,
        category: type ? type.category_id : null,
        colour: poi.colour || type?.color || "#ff0000",
        shape: poi.shape || type?.shape || "circle",
        x: poi.x,
        y: poi.y,
        description: poi.description || "",
        icon_file: poi.icon_file || "",
        map_id: poi.map_id,
        typeName: type?.name || "",
        label_border: typeof type?.label_border === "undefined" ? 1 : type.label_border,
        label_border_colour: type?.label_border_colour || "#000000",
        label_bg_colour: type?.label_bg_colour || "#000000",
        image_url: poi.image_url || "",
      };
    });
  } else {
    poisToShow = pois;
  }

  const mapKey = mapData && imageSize ? `${mapData.id}_${imageSize[0]}x${imageSize[1]}_${offlineMode}` : 'no-map';

  let categoryId = null;
  if (selectedCategory && customCategories) {
    const foundCat = customCategories.find(cat => (cat.name || cat).toLowerCase() === selectedCategory.toLowerCase());
    if (foundCat) categoryId = foundCat.id || foundCat;
  }

  return (
    <POISelectedIdContext.Provider value={[selectedPoiId, setSelectedPoiId]}>
      <div
        ref={mapDivRef}
        style={{ flex: "1 1 0%", minHeight: 0, minWidth: 0, position: "relative" }}
      >
        {!isEmbed && (
          <div
            className="map-controls-cluster"
            style={
              isMobile
                ? {
                  position: 'fixed',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1600,
                  bottom: `${toolboxBottom}px`,
                  background: '#222f',
                  borderRadius: 16,
                  padding: '4px 18px',
                  boxShadow: '0 2px 14px #000b',
                  display: 'flex',
                  gap: 6
                }
                : undefined
            }
          >
            <button
              className={`map-controls-btn ${showFound ? "active" : ""}`}
              onClick={() => setShowFound(f => !f)}
              title={showFound ? "Hide found POIs" : "Show found POIs"}
            >
              <Eye />
            </button>

            <OfflineBackup mapId={mapData.id} user={user} noRestore />

            <OfflineUploadToServer mapId={mapData.id} user={user} />
          </div>
        )}
        <MapContainer
          key={mapKey}
          crs={L.CRS.Simple}
          bounds={[[0, 0], imageSize]}
          style={{ height: '100%', width: '100%' }}
          minZoom={-4}
          maxZoom={4}
          zoom={0}
          zoomSnap={0.1}
          zoomDelta={0.5}
          scrollWheelZoom={true}
          zoomControl={!isMobile}
          whenCreated={mapInstance => { leafletMapRef.current = mapInstance; }}
        >
          <GeomanControl
            enabled={
              isMapOwnerOrFounder &&
              !showCreatePOIModal &&
              !editPOIModalOpen &&
              !infoPOIModalOpen
            }
            setIsGeomanActive={setIsGeomanActive}
          />
          <SetZoomControlPosition />
          <MouseCoords onHover={handleMouseCoord} />
          {isMapOwnerOrFounder && !isGeomanActive && (
            <ClickHandler onClick={wrapIfModalOpen((coord, event) => handleClick(coord, event))} enabled={true} />
          )}
          {!isMapOwnerOrFounder && !isGeomanActive && (
            <ClickHandler onClick={wrapIfModalOpen((coord, event) => handleClick(coord, event))} enabled={true} />
          )}
          <MapClickPopupCloser />
          <GeomanShapesSync
            map={leafletMapRef.current}
            mapId={mapData.id}
            userId={user?.id}
            shapeLayerMap={shapeLayerMap}
            allLayersById={allLayersById}
            onZonesChanged={onZonesChanged}
            setIsGeomanActive={setIsGeomanActive}
            onShapeClick={isMapOwnerOrFounder ? (shape, layer) => {
              setIsGeomanActive(true);
              setSelectedShape({
                ...shape,
                strokeColor: layer?.options.color ?? shape.strokeColor,
                fillColor: layer?.options.fillColor ?? shape.fillColor,
                strokeOpacity: layer?.options.opacity ?? shape.strokeOpacity,
                fillOpacity: layer?.options.fillOpacity ?? shape.fillOpacity,
              });
              setSelectedLayer(layer || (shape && shapeLayerMap.current[shape.id]) || null);
            } : undefined}
            visibleZoneIds={visibleZoneIds}
          />
          <ImageOverlay
            url={mapImageToShow}
            bounds={[[0, 0], imageSize]}
          />
          <FitImageBounds bounds={[[0, 0], imageSize]} />
          <POILayer
            pois={poisToShow}
            foundPOIs={foundPOIsState}
            showFound={showFound}
            customCategories={customCategories}
            customTypes={customTypes}
            hoveredPoiId={hoveredPoiId}
            hoveredType={hoveredType}
            setHoveredPoiId={setHoveredPoiId}
            visibleTypes={visibleTypes}
            visibleCategories={visibleCategories}
            showAllPoiLabels={showAllPoiLabels}
            typeColours={{}}
            onCategoryUpdate={onCategoryUpdate}
            onPOIUpdate={handlePOIUpdate}
            isMapOwnerOrFounder={offlineMode ? false : isMapOwnerOrFounder}
            mapId={mapData.id}
            user={user}
            mapOwnerId={mapOwnerId}
            onToggleFound={(poiId) => {
              setFoundPOIsState(prev => {
                const isFound = prev.includes(poiId);
                if (isFound) return prev.filter(id => id !== poiId);
                return [...prev, poiId];
              });
            }}
            hideSidebarHover={hideSidebarHover}
            offline={offlineMode}
            onOpenEditModal={() => setEditPOIModalOpen(true)}
            onCloseEditModal={() => setEditPOIModalOpen(false)}
            onOpenInfoModal={() => setInfoPOIModalOpen(true)}
            onCloseInfoModal={() => setInfoPOIModalOpen(false)}
          />
          {shareCoord && (
            <CoordPopup coord={shareCoord} onCopy={handleCopyCoord} copied={copiedCoord} />
          )}
        </MapContainer>
        <OfflineCreatePOI
          show={showCreatePOIModal}
          onClose={() => setPendingPOI(null)}
          poiName={poiName}
          setPoiName={setPoiName}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          localTypeColour={localTypeColour}
          setLocalTypeColour={setLocalTypeColour}
          colourLocked={colourLocked}
          handleUnlockColour={() => setColourLocked(false)}
          shape={shape}
          setShape={setShape}
          localLabelBorder={localLabelBorder}
          setLocalLabelBorder={setLocalLabelBorder}
          localLabelBorderColour={localLabelBorderColour}
          setLocalLabelBorderColour={setLocalLabelBorderColour}
          description={description}
          setDescription={setDescription}
          iconFile={iconFile}
          setIconFile={setIconFile}
          iconPreview={iconPreview}
          setIconPreview={setIconPreview}
          poiImageFile={poiImageFile}
          setPoiImageFile={setPoiImageFile}
          poiImagePreview={poiImagePreview}
          setPoiImagePreview={setPoiImagePreview}
          fileInputRef={fileInputRef}
          poiImageInputRef={poiImageInputRef}
          savePOI={savePOI}
          disabled={!selectedType}
          customCategories={customCategories}
          customTypes={customTypes}
          imageUrl={imageUrl}
          categoryId={categoryId}
        />
        {selectedShape && selectedLayer && (
          <ShapeEditToolbar
            shape={selectedShape}
            layer={selectedLayer}
            onChange={updatedShape => setSelectedShape(updatedShape)}
            onSave={() => handleShapeToolbarSave(selectedShape, selectedLayer)}
            onClose={() => {
              setSelectedShape(null);
              setSelectedLayer(null);
              setIsGeomanActive(false);
              setPendingPOI(null);
            }}
            onDelete={() => handleShapeToolbarDelete(selectedShape, selectedLayer)}
            saving={savingShape}
            canEdit={true}
          />
        )}
      </div>
    </POISelectedIdContext.Provider>
  );
}

export default InteractiveMap;
